"use client";

import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin } from "lucide-react";

/**
 * Contact Page — Full UI/UX overhaul
 * - Glowing gold on black theme
 * - Animated background orbs + subtle flame + shimmer effects
 * - Beautiful responsive form with client-side validation, graceful fallback (mailto)
 * - Accessible labels, states, and success/failure feedback
 *
 * Notes: This component POSTS to /api/contact. If you don't have that route yet,
 * either create a server handler or the form will gracefully fall back to a mailto link.
 */

export default function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<null | "idle" | "sending" | "success" | "error">("idle");
  const [errorText, setErrorText] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  const gold = "#ffd86b";

  const validate = () => {
    if (!name.trim()) return "Please enter your name.";
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email.";
    if (!subject.trim()) return "Please add a subject.";
    if (!message.trim() || message.trim().length < 8) return "Please write a longer message (8+ chars).";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorText(null);
    const v = validate();
    if (v) {
      setErrorText(v);
      setStatus("error");
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, subject, message }),
      });

      if (res.ok) {
        setStatus("success");
        setName("");
        setEmail("");
        setSubject("");
        setMessage("");
        setErrorText(null);
      } else {
        const json = await res.json().catch(() => ({}));
        setErrorText(json?.error || "Server failed to process your message. Try emailing us directly.");
        setStatus("error");
      }
    } catch (err: any) {
      console.error("contact submit error", err);
      setErrorText("Network error — try again or email hello@thestoryclub.com");
      setStatus("error");
    }
  };

  const mailtoHref = `mailto:hello@thestoryclub.online?subject=${encodeURIComponent(
    subject || "Contact from The Story Club"
  )}&body=${encodeURIComponent(`Name: ${name}\nEmail: ${email}\n\n${message}`)}`;

  return (
    <div className="min-h-screen bg-black text-slate-100 antialiased">
      <style>{`
        :root {
          --gold: ${gold};
          --gold-soft: rgba(255,184,77,0.06);
          --panel: rgba(255,255,255,0.02);
        }

        /* animated orbs */
        .orb {
          position: absolute;
          border-radius: 9999px;
          filter: blur(32px);
          opacity: 0.18;
          pointer-events: none;
          mix-blend-mode: screen;
          transform: translate3d(0,0,0);
          animation: floaty 14s ease-in-out infinite;
        }
        .orb.one { width: 360px; height: 360px; left: -120px; top: -60px; background: radial-gradient(circle at 20% 30%, rgba(255,200,60,0.14), transparent 30%); }
        .orb.two { width: 260px; height: 260px; right: -80px; top: 40px; background: radial-gradient(circle at 70% 40%, rgba(255,150,40,0.12), transparent 30%); animation-duration: 18s; }
        @keyframes floaty { 0% { transform: translateY(0) translateX(0); } 50% { transform: translateY(-18px) translateX(8px); } 100% { transform: translateY(0) translateX(0); } }

        /* subtle flame under hero */
        .flame {
          position: absolute;
          width: 480px;
          height: 120px;
          left: 50%;
          transform: translateX(-50%);
          bottom: -40px;
          border-radius: 9999px;
          background: radial-gradient(circle at 50% 45%, rgba(255,190,60,0.06), rgba(255,150,40,0.02));
          filter: blur(22px);
          animation: flicker 2.8s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes flicker {
          0% { transform: translateX(-50%) translateY(0) scaleX(1); opacity: 0.72; }
          50% { transform: translateX(-50%) translateY(-8px) scaleX(1.03); opacity: 0.95; }
          100% { transform: translateX(-50%) translateY(0) scaleX(1); opacity: 0.72; }
        }

        /* glowing header */
        .glow-title {
          color: var(--gold);
          text-shadow: 0 0 10px rgba(255,200,60,0.18), 0 8px 36px rgba(255,150,40,0.04);
        }

        /* glass panel */
        .glass {
          background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.45));
          border: 1px solid rgba(255,184,77,0.04);
          backdrop-filter: blur(6px);
        }

        /* card hover */
        .card {
          transition: transform .28s cubic-bezier(.2,.9,.3,1), box-shadow .28s;
        }
        .card:hover {
          transform: translateY(-6px);
          box-shadow: 0 16px 50px rgba(255,165,0,0.06);
        }

        /* shimmer line */
        .shimmer::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.03), transparent);
          transform: translateX(-100%);
          transition: transform .9s ease;
        }
        .shimmer:hover::after { transform: translateX(100%); }

        /* small micro-animations */
        .fade-up { transform: translateY(8px); opacity: 0; animation: fu .6s forwards; }
        .fade-up.delay-1 { animation-delay: .08s; }
        .fade-up.delay-2 { animation-delay: .16s; }
        @keyframes fu { to { transform: translateY(0); opacity: 1 } }

        /* inputs */
        input, textarea {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,184,77,0.04);
          color: #e6e6e6;
        }
        input:focus, textarea:focus {
          outline: none;
          border-color: var(--gold);
          box-shadow: 0 6px 28px rgba(255,170,0,0.04);
        }

        /* responsive tweaks */
        @media (max-width: 768px) {
          .orb, .flame { display: none; }
        }
      `}</style>

      <div className="relative overflow-hidden">
        <div className="orb one" aria-hidden />
        <div className="orb two" aria-hidden />

        <main className="max-w-6xl mx-auto px-6 py-16 relative z-10">
          {/* HERO */}
          <header className="relative glass rounded-3xl p-8 md:p-12 overflow-hidden">
            <div className="flame" aria-hidden />
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="flex-1">
                <h1 className="text-4xl md:text-5xl font-serif font-extrabold glow-title">
                  Get in Touch — The Story Club
                </h1>
                <p className="mt-3 text-slate-300 max-w-2xl">
                  Have questions, feedback, or a story idea? We’d love to hear from you. Fill the form below and our
                  team will respond within 24–48 hours. Prefer direct email? Use hello@thestoryclub.online.
                </p>

                <div className="mt-6 flex flex-wrap gap-3 items-center">
                  <a href="mailto:hello@thestoryclub.com" className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-yellow-500/6 border border-yellow-500/8">
                    <Mail className="w-4 h-4 text-[#ffd86b]" /> hello@thestoryclub.online
                  </a>
                  <a href="tel:+1234567890" className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-transparent border border-slate-800">
                    <Phone className="w-4 h-4 text-slate-300" /> +1 (234) 567-890
                  </a>
                </div>
              </div>

              <div className="w-full lg:w-96">
                <div className="glass rounded-2xl p-4 card">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-md bg-gradient-to-br from-yellow-500 to-amber-400 flex items-center justify-center text-black font-semibold">TS</div>
                    <div>
                      <div className="text-xs text-slate-400">Support</div>
                      <div className="text-sm text-slate-100">We usually reply within 24–48 hours</div>
                    </div>
                  </div>

                  <div className="mt-4 text-sm text-slate-300">
                    For urgent matters, include <strong>URGENT</strong> in the subject and we will prioritize your message.
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* MAIN GRID */}
          <section className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* FORM */}
            <div className="glass rounded-2xl p-8 card shimmer">
              <h2 className="text-2xl font-semibold text-[#ffd86b]">Send Us a Message</h2>
              <p className="mt-2 text-slate-300 text-sm">Tell us about your idea, feedback, or partnership inquiry.</p>

              <form ref={formRef} onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="name" className="text-xs text-slate-300">Your Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Doe"
                    className="mt-2 py-3 text-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-xs text-slate-300">Your Email</Label>
                  <Input
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    type="email"
                    className="mt-2 py-3 text-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="subject" className="text-xs text-slate-300">Subject</Label>
                  <Input
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Story Idea / Support / Partnership"
                    className="mt-2 py-3 text-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-xs text-slate-300">Your Message</Label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Share your idea or question..."
                    rows={6}
                    className="mt-2 py-3 text-sm"
                  />
                </div>

                {errorText && (
                  <div className="text-sm text-red-400">
                    {errorText}
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Button
                    type="submit"
                    className={`px-6 py-3 rounded-full text-black bg-gradient-to-r from-yellow-500 to-amber-400 ${status === "sending" ? "opacity-80 cursor-wait" : ""}`}
                    disabled={status === "sending"}
                  >
                    {status === "sending" ? "Sending…" : "Send Message"}
                  </Button>

                  {/* graceful fallback to mailto if /api/contact doesn't exist */}
                  <a
                    href={mailtoHref}
                    onClick={() => {
                      /* track fallback clicks if desired */
                    }}
                    className="text-sm text-slate-300 underline"
                  >
                    Or email us directly
                  </a>

                  {/* small status */}
                  <div className="ml-auto text-sm">
                    {status === "success" && <span className="text-yellow-300">Message sent — thank you! ✨</span>}
                    {status === "error" && <span className="text-red-400">Failed to send — try direct email.</span>}
                  </div>
                </div>
              </form>
            </div>

            {/* CONTACT INFO + MAP */}
            <div className="space-y-6">
              <div className="glass rounded-2xl p-6 card">
                <h3 className="text-lg font-semibold text-[#ffd86b]">Find Us</h3>
                <div className="mt-4 space-y-4 text-slate-300">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-black/50 flex items-center justify-center border border-yellow-600/6">
                      <Mail className="w-5 h-5 text-[#ffd86b]" />
                    </div>
                    <div>
                      <div className="text-sm">Email</div>
                      <a href="mailto:hello@thestoryclub.com" className="text-slate-100">hello@thestoryclub.com</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-black/50 flex items-center justify-center border border-yellow-600/6">
                      <Phone className="w-5 h-5 text-[#ffd86b]" />
                    </div>
                    <div>
                      <div className="text-sm">Phone</div>
                      <a href="tel:+1234567890" className="text-slate-100">+1 (234) 567-890</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-black/50 flex items-center justify-center border border-yellow-600/6">
                      <MapPin className="w-5 h-5 text-[#ffd86b]" />
                    </div>
                    <div>
                      <div className="text-sm">Address</div>
                      <div className="text-slate-300 text-sm mt-1">123 Imagination Lane, Storyville, CA 90210, USA</div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 text-xs text-slate-400">Office hours: Mon–Fri, 9am–6pm PT</div>
              </div>

              <div className="glass rounded-2xl overflow-hidden border border-yellow-600/6">
                {/* Map placeholder — replace with an embedded map or image if you want */}
                <div className="w-full h-64 bg-gradient-to-tr from-black/60 to-black/30 flex items-center justify-center">
                  <div className="text-center text-slate-500">
                    <div className="text-sm">Map</div>
                    <div className="mt-2 text-xs">Visit us</div>
                  </div>
                </div>
              </div>

              <div className="glass rounded-2xl p-4 card flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-400">Follow</div>
                  <div className="text-sm text-slate-100">Social updates, latest features & community highlights</div>
                </div>
                <div className="flex gap-3">
                  <a href="#" className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center border border-yellow-600/6 text-slate-100">F</a>
                  <a href="#" className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center border border-yellow-600/6 text-slate-100">I</a>
                  <a href="#" className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center border border-yellow-600/6 text-slate-100">Y</a>
                </div>
              </div>
            </div>
          </section>

          <footer className="mt-12 text-center text-slate-500 text-sm">
            © {new Date().getFullYear()} The Story Club — crafted with care, powered by responsible AI.
          </footer>
        </main>
      </div>
    </div>
  );
}
